import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import LightbulbIcon from '@atlaskit/icon/core/lightbulb';

import { fontSize } from '@/components/signup/utils/font-size';
import { wacToken } from '@/libraries/design/foundations';

/**
 * Inline, non-blocking nudge shown beneath the signup email input when the user
 * has entered a personal-domain email (e.g. gmail.com) while the
 * `recommend-work-email-signup` experiment is enrolled in treatment.
 *
 * Renders a light-blue callout with a lightbulb icon, a bold heading, and a
 * short body line — matching the treatment design in the project poster. It
 * recommends using a work email but does not prevent submission; the user is
 * still free to continue with the personal email they entered.
 */
export const RecommendWorkEmailNudge = () => {
	return (
		<div css={calloutCss} role="status">
			<span css={iconCss} aria-hidden="true">
				<LightbulbIcon label="" color="currentColor" />
			</span>
			<div css={contentCss}>
				<div css={titleCss}>
					<FormattedMessage
						id="signupForm.accountCreation.recommendWorkEmail.title"
						defaultMessage="Unlock Jira for work"
					/>
				</div>
				<div css={bodyCss}>
					<FormattedMessage
						id="signupForm.accountCreation.recommendWorkEmail.body"
						defaultMessage="Use your work email to bring AI, automation, and your team into Jira."
					/>
				</div>
			</div>
		</div>
	);
};

const calloutCss = css({
	display: 'flex',
	gap: 12,
	alignItems: 'flex-start',
	marginBlockStart: 8,
	padding: 16,
	borderRadius: 8,
	backgroundColor: wacToken('color.extended.blue100'),
	fontFamily: wacToken('font.charlie.text'),
});

const iconCss = css({
	display: 'flex',
	flexShrink: 0,
	color: wacToken('color.extended.blue700'),
});

const contentCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: 4,
});

const titleCss = css({
	fontSize: fontSize.sm,
	fontWeight: 600,
	lineHeight: 1.4,
	color: wacToken('color.neutral.light1000'),
});

const bodyCss = css({
	fontSize: fontSize.sm,
	lineHeight: 1.5,
	color: wacToken('color.neutral.light800'),
});
